import api from './api';
import { downloadResponse } from '../utils/downloadResponse';
import { ApiResponse, Pagination } from '../types';
import { Revenue, RevenueStats } from '../types/revenues';

export interface RevenueListResponse {
  revenues: Revenue[];
  pagination: Pagination;
}

// ─── CRUD ────────────────────────────────────────────────────────────────────

/** The filters the list and its exports share. */
export interface RevenueFilters {
  categoryId?: string;
  subCategoryId?: string;
  method?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  includeReversed?: boolean;
}

const revenueQuery = (params: RevenueFilters & { page?: number; limit?: number } = {}) => {
  const sp = new URLSearchParams();
  if (params.categoryId) sp.append('categoryId', params.categoryId);
  if (params.includeReversed) sp.append('includeReversed', 'true');
  if (params.subCategoryId) sp.append('subCategoryId', params.subCategoryId);
  if (params.method) sp.append('method', params.method);
  if (params.search) sp.append('search', params.search);
  if (params.dateFrom) sp.append('dateFrom', params.dateFrom);
  if (params.dateTo) sp.append('dateTo', params.dateTo);
  if (params.page) sp.append('page', String(params.page));
  if (params.limit) sp.append('limit', String(params.limit));
  return sp.toString();
};

export const getRevenues = async (params?: RevenueFilters & { page?: number; limit?: number }): Promise<RevenueListResponse> => {
  const response = await api.get<ApiResponse<RevenueListResponse>>(`/revenues?${revenueQuery(params)}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const getRevenueById = async (id: string): Promise<Revenue> => {
  const response = await api.get<ApiResponse<Revenue>>(`/revenues/${id}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

export const createRevenue = async (data: FormData): Promise<Revenue> => {
  const response = await api.post<ApiResponse<Revenue>>('/revenues', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

/**
 * Reverse (soft-delete) a revenue. Posts a compensating OUT ledger entry.
 * `reason` is required — it's the memo on the reversal row for audit.
 */
export const reverseRevenue = async (id: string, reason: string): Promise<void> => {
  const response = await api.post<ApiResponse>(`/revenues/${id}/reverse`, { reason });
  if (!response.data.success) throw new Error(response.data.message);
};

export const getRevenueStats = async (): Promise<RevenueStats> => {
  const response = await api.get<ApiResponse<RevenueStats>>('/revenues/stats');
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

/** The filtered revenue as a CSV. */
export const exportRevenuesCsv = async (filters: RevenueFilters) =>
  downloadResponse(await api.get(`/revenues/export/csv?${revenueQuery(filters)}`, { responseType: 'blob' }), 'revenue.csv');

/** The filtered revenue with its totals, for the Revenue Report the browser prints. */
export interface RevenueReport {
  includeReversed: boolean;
  rows: Array<{
    date: string;
    voucher: string;
    title: string;
    category: string;
    from: string;
    method: string;
    reference: string;
    amount: number;
    reversed: boolean;
  }>;
  /** What the standing entries brought in. */
  received: number;
  entries: number;
  reversed: number;
}

export const getRevenueReport = async (filters: RevenueFilters): Promise<RevenueReport> => {
  const response = await api.get<ApiResponse<RevenueReport>>(`/revenues/export/report?${revenueQuery(filters)}`);
  if (!response.data.success) throw new Error(response.data.message);
  return response.data.data!;
};

const revenueService = {
  exportRevenuesCsv,
  getRevenueReport,
  getRevenues,
  getRevenueById,
  createRevenue,
  reverseRevenue,
  getRevenueStats,
};

export default revenueService;
